/**
 * Column Menu Hooks - StreamLain Extension System
 *
 * Allows StreamLain to register additional menu items for database column headers
 * without modifying BlockSuite source files.
 *
 * This uses a window global to avoid circular dependencies between
 * BlockSuite (library) and Frontend (application).
 */
import type { DatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';
import {
  menu,
  type MenuConfig,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import type { Property } from '@blocksuite/data-view';
import type {
  TableProperty,
  TableSingleView,
} from '@blocksuite/data-view/view-presets';
import { html } from 'lit/static-html.js';

/**
 * Interface for column menu hooks (must match BlockSuite's expectation).
 * Visibility is owned by the returned subMenu's own `hide` closure.
 */
export interface ColumnMenuHook {
  name: string;
  getSubMenu: (
    column: TableProperty,
    tableViewManager: TableSingleView
  ) => ReturnType<typeof menu.subMenu>;
  onAfterTypeChange?: (
    column: TableProperty,
    tableViewManager: TableSingleView,
    newType: string,
    anchor: HTMLElement
  ) => void;
}

// Declare the global type
declare global {
  interface Window {
    __streamlainColumnMenuHooks?: ColumnMenuHook[];
  }
}

/**
 * Register a column menu hook (via window global)
 */
export function registerColumnMenuHook(hook: ColumnMenuHook): void {
  if (!window.__streamlainColumnMenuHooks) {
    window.__streamlainColumnMenuHooks = [];
  }
  window.__streamlainColumnMenuHooks.push(hook);
}

// ============================================================================
// StreamLain Menu Hooks Registration
// ============================================================================

/**
 * Relation Config — submenu variant (rendered inside the column-header menu).
 * Uses the Lit <relation-config-menu> element as a submenu overlay item.
 */
function createRelationConfigMenu(
  column: Property,
  tableViewManager: TableSingleView
): MenuConfig {
  return menuInstance => {
    const dataSource = tableViewManager.manager
      .dataSource as DatabaseBlockDataSource;
    const store = dataSource.doc;
    const currentDatabaseId = dataSource.blockId ?? '';

    return html`
      <relation-config-menu
        .store="${store}"
        .currentDatabaseId="${currentDatabaseId}"
        .propertyId="${column.id}"
        .data="${column.data$.value}"
        .sourceColumnName="${column.name$.value}"
        .sourceColumnNames="${tableViewManager.propertiesRaw$.value
          .filter(property => property.id !== column.id)
          .map(property => property.name$.value)}"
        .onPreviewSourceColumnName="${(name: string) =>
          previewOpenColumnMenuNameField(column, name)}"
        .onCommitSourceColumnName="${(name: string) => column.nameSet(name)}"
        .onUpdate="${(data: Record<string, unknown>) => {
          column.dataUpdate(() => data);
        }}"
        .onClose="${() => menuInstance.close()}"
      ></relation-config-menu>
    `;
  };
}

/**
 * Auto-expand the "Relation Config" submenu inside the still-open
 * column-header menu. After the user picks Type=Relation from the Type
 * submenu, BlockSuite closes the Type submenu and returns focus to the
 * main column menu. We wait for the main menu's DOM to update (the
 * Relation Config row is `hide`-gated on `type$ === 'relation'`), then
 * click its submenu button — same code path as a real user click, so
 * positioning / theming / focus all stay native.
 */
function expandRelationConfigSubMenu(): boolean {
  // BlockSuite renders nested menus as siblings; the last <affine-menu>
  // in DOM order is the main (column-header) menu now that the Type
  // submenu has unmounted.
  const menus = document.querySelectorAll('affine-menu');
  const mainMenu = menus[menus.length - 1];
  if (!mainMenu) return false;
  const subMenus = mainMenu.querySelectorAll('affine-menu-sub-menu');
  for (const sub of Array.from(subMenus)) {
    if (sub.textContent?.trim().startsWith('Relation Config')) {
      (sub as HTMLElement).click();
      return true;
    }
  }
  return false;
}

function popRelationConfigMenu(
  column: TableProperty,
  tableViewManager: TableSingleView,
  anchor: HTMLElement
): void {
  popMenu(popupTargetFromElement(anchor), {
    options: {
      preferDesktop: true,
      items: [createRelationConfigMenu(column, tableViewManager)],
    },
    placement: 'right-start',
  });
}

function previewOpenColumnMenuNameField(column: Property, name: string): void {
  const currentName = column.name$.value;
  for (const menuElement of Array.from(
    document.querySelectorAll('affine-menu')
  )) {
    const input = menuElement.querySelector<HTMLInputElement>(
      'affine-menu-input input'
    );
    if (!input) continue;
    if (input.value !== currentName && input.value !== 'Relation') continue;
    input.value = name;
    return;
  }
}

/**
 * Rollup Config Menu (E018-S008-T009)
 */
function createRollupConfigMenu(
  column: Property,
  tableViewManager: TableSingleView
): MenuConfig {
  return menuInstance => {
    const dataSource = tableViewManager.manager
      .dataSource as DatabaseBlockDataSource;
    const store = dataSource.doc;
    const currentDatabaseId = dataSource.blockId ?? '';

    return html`
      <rollup-config-menu
        .store="${store}"
        .currentDatabaseId="${currentDatabaseId}"
        .propertyId="${column.id}"
        .data="${column.data$.value}"
        .onUpdate="${(data: Record<string, unknown>) => {
          column.dataUpdate(() => data);
        }}"
        .onClose="${() => menuInstance.close()}"
      ></rollup-config-menu>
    `;
  };
}

/**
 * Initialize StreamLain column menu hooks
 * Call this during app initialization
 */
export function initColumnMenuHooks(): void {
  // Relation Config
  registerColumnMenuHook({
    name: 'Relation Config',
    getSubMenu: (column, tableViewManager) =>
      menu.subMenu({
        name: 'Relation Config',
        hide: () => !column.dataUpdate || column.type$.value !== 'relation',
        options: {
          items: [createRelationConfigMenu(column, tableViewManager)],
        },
      }),
    onAfterTypeChange: (column, tableViewManager, newType, anchor) => {
      if (newType !== 'relation') return;
      // Wait past: (a) Type submenu close animation, (b) main menu re-render
      // after column.type$ flips to 'relation' so the Relation Config row
      // stops being `hide`-gated. Two rAFs is enough in practice.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (expandRelationConfigSubMenu()) return;
          popRelationConfigMenu(column, tableViewManager, anchor);
        })
      );
    },
  });

  // Rollup Config
  registerColumnMenuHook({
    name: 'Rollup Config',
    getSubMenu: (column, tableViewManager) =>
      menu.subMenu({
        name: 'Rollup Config',
        hide: () => !column.dataUpdate || column.type$.value !== 'rollup',
        options: {
          items: [createRollupConfigMenu(column, tableViewManager)],
        },
      }),
  });
}

// Auto-initialize when this module is imported
initColumnMenuHooks();
